import { useState, useEffect } from "react";
import { bookAmenity } from "../../../services/amenityService";
import { getStoredAuthUser } from "../../../services/authService";
import { getMyProfile } from "../../../services/userService";
import FormField from "../../../components/ui/FormField";
import SelectDropdown from "../../../components/ui/SelectDropdown";
import { DatePicker, TimePicker } from "../../../components/ui/DateTimePickers";
import Button from "../../../components/ui/Button";
import { ErrorCard } from "../../../components/ui/FeedbackStates";
import styles from "../../../components/users/UserForm.module.css";

const ResidentBookAmenityModal = ({ amenity, onClose, onSuccess }) => {
  const user = getStoredAuthUser();
  
  const [form, setForm] = useState({
    flat_id: "",
    booking_date: "",
    start_time: "09:00",
    end_time: "10:00",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userFlats, setUserFlats] = useState([]);

  useEffect(() => {
    // Fetch profile to get flat_mappings
    getMyProfile()
      .then((profile) => {
        const mappings = profile?.flat_mappings || [];
        setUserFlats(mappings);
        if (mappings.length === 1) {
          setForm((prev) => ({ ...prev, flat_id: String(mappings[0].flat_id || mappings[0].flatId) }));
        }
      })
      .catch((err) => console.error("Failed to load profile", err));
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.flat_id || !form.booking_date || !form.start_time || !form.end_time) {
      setError("All fields are required.");
      return;
    }

    if (form.start_time >= form.end_time) {
      setError("End time must be after start time.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        amenity_id: amenity.amenity_id,
        society_id: amenity.society_id,
        user_id: user.user_id,
        flat_id: form.flat_id,
        booking_date: form.booking_date,
        start_time: new Date(`${form.booking_date}T${form.start_time}`).toISOString(),
        end_time: new Date(`${form.booking_date}T${form.end_time}`).toISOString(),
      };
      await bookAmenity(payload);
      onSuccess && onSuccess();
    } catch (err) {
      console.error("Booking error:", err);
      const errorMessage = err.data?.error || err.message || "Failed to book amenity.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div className={styles.headerRow}>
        <div>
          <h3 className={styles.headerTitle} style={{ margin: "0 0 4px 0", fontSize: "17px", fontWeight: 700 }}>
            Book {amenity?.name}
          </h3>
          <p className={styles.headerHint} style={{ margin: 0, fontSize: "13px", color: "var(--color-text-muted)" }}>
            Select your assigned unit and preferred reservation slot.
          </p>
        </div>
      </div>

      {error && <ErrorCard title="Reservation Error" message={error} />}
      
      <div className={styles.grid}>
        <FormField label="Your Unit / Flat" required>
          <SelectDropdown 
            value={form.flat_id} 
            onChange={(e) => handleChange({ target: { name: 'flat_id', value: e.target.value } })} 
            options={[
              { label: "-- Select Flat --", value: "" },
              ...userFlats.map((fm) => ({
                label: fm.flat_number ? `Flat ${fm.flat_number}` : `Flat ${fm.flat_id || fm.flatId}`,
                value: String(fm.flat_id || fm.flatId),
              }))
            ]}
          />
        </FormField>

        <div className={styles.fullSpan}>
          <FormField label="Booking Date" required>
            <DatePicker 
              name="booking_date" 
              value={form.booking_date} 
              onChange={(e) => handleChange({ target: { name: 'booking_date', value: e.target.value } })} 
              min={new Date().toISOString().split("T")[0]}
            />
          </FormField>
        </div>

        <FormField label="Start Time" required>
          <TimePicker 
            name="start_time" 
            value={form.start_time} 
            onChange={(e) => handleChange({ target: { name: 'start_time', value: e.target.value } })} 
          />
        </FormField>

        <FormField label="End Time" required>
          <TimePicker 
            name="end_time" 
            value={form.end_time} 
            onChange={(e) => handleChange({ target: { name: 'end_time', value: e.target.value } })} 
          />
        </FormField>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
        <Button 
          type="button" 
          variant="outline"
          onClick={onClose} 
        >
          Cancel
        </Button>
        <Button type="submit" disabled={loading} loading={loading}>
          Confirm Booking
        </Button>
      </div>
    </form>
  );
};

export default ResidentBookAmenityModal;
